import { defineComponent } from 'vue';
import axios, { AxiosResponse } from 'axios';

import mixin from './mixin';
import type { APIItem, OrderItem, FilterItem, QueryModel, Facet, FacetOption, Response } from '../shared/types';
import constants from '../shared/constants';

import Skeleton from './components/Skeleton.vue';

export default defineComponent({
    components: { Skeleton },
    mixins: [mixin],
    data() {
        return {
            dataApi: '' as string,
            dataLimit: 15 as number,
            dataSortKey: '' as string,
            dataSortDirection: '' as string,
            dataAmountsSentence: '' as string,
            dataWidget: false as boolean,
            dataOverruleIds: [] as string[],
            dataFixedFilterItems: [] as FilterItem[],
            dataCulture: 'nl-NL' as string,
            dataStructuredDataScriptId: '' as string,
            dataStructuredDataPageName: '' as string,
            dataStructuredDataPageDescription: '' as string,
            dataStructuredDataPageUrl: '' as string,
            loading: false as boolean,
            loadingMore: false as boolean,
            debounceSetLoading: (_state: boolean) => { },
            changeSearchText: () => { },
            data: {
                items: [] as APIItem[],
                facets: [] as Facet[],
                queryCount: 0 as number,
            } as Response<APIItem>,
            page: 1 as number,
            pageLimit: 100 as number,
            searchText: '' as string,
            filterParams: null as URLSearchParams | null,
            error: false as boolean,

            constants: constants,
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet(): number {
            return this.data.facets.length;
        },
        itemsSet(): number {
            return this.data.items.length;
        },
        filtersChosen(): boolean | number {
            return (this.facetsSet && this.data.facets.some(facet => facet.values.length)) || this.dataOverruleIds.length;
        },
        filtersActive(): boolean | string {
            return (this.facetsSet && this.data.facets.some(facet => facet.selected)) || this.searchText;
        },
        orderItems(): OrderItem[] {
            if (!this.dataSortKey && !this.dataSortDirection || this.searchText) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems(): FilterItem[] {
            const fixedFilterItems = this.dataFixedFilterItems;

            if (this.filterParams) {
                return [
                    ...fixedFilterItems,
                    ...Array.from(this.filterParams.entries()).map(([key, value]) => ({
                        Key: key,
                        Values: value.split(','),
                        Operator: constants.operators.in,
                    }))
                ];
            }
            if (this.dataOverruleIds.length) {
                return [
                    ...fixedFilterItems,
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in,
                    }
                ]
            }
            return this.data.facets.reduce((items: FilterItem[], facet: Facet) => {
                if (facet.values.length) {
                    items.push({
                        Key: facet.alias,
                        Values: facet.values,
                        Operator: this.constants.operators.in,
                    });
                }
                return items;
            }, [...fixedFilterItems]);
        },
        queryModel(): QueryModel {
            return {
                Limit: this.dataLimit,
                Offset: this.itemsSet && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText
            }
        },
        amountsSentence(): string {
            if (!this.data || !this.data.queryCount) return '';
            return this.dataAmountsSentence
                .replace(this.constants.magicStrings.queryCount, this.data.items.length.toString())
                .replace(this.constants.magicStrings.totalCount, this.data.queryCount.toString());
        },
    },
    async created() {
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
        // Initialize the debounced loading function.
        this.debounceSetLoading = this.createDebouncedLoading();
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    },
    methods: {
        async init(): Promise<void> {
            const structuredDataElement = (this.$el as HTMLElement).querySelector<HTMLElement>('[data-structured-data-script-id]');

            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget;
            if (this.$el.dataset.overruleIds) this.dataOverruleIds = this.$el.dataset.overruleIds.split(',');
            if (this.$el.dataset.filterKey && this.$el.dataset.filterValue) {
                this.dataFixedFilterItems = [
                    {
                        Key: this.$el.dataset.filterKey,
                        Values: [this.$el.dataset.filterValue],
                        Operator: this.constants.operators.in,
                    }
                ];
            }
            if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.dataCulture;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortKey) this.dataSortKey = this.$el.dataset.sortKey;
            if (this.$el.dataset.sortDirection) this.dataSortDirection = this.$el.dataset.sortDirection;
            if (this.$el.dataset.amountsSentence) this.dataAmountsSentence = this.$el.dataset.amountsSentence;
            if (this.$el.dataset.structuredDataScriptId) this.dataStructuredDataScriptId = this.$el.dataset.structuredDataScriptId;
            if (this.$el.dataset.structuredDataPageName) this.dataStructuredDataPageName = this.$el.dataset.structuredDataPageName;
            if (this.$el.dataset.structuredDataPageDescription) this.dataStructuredDataPageDescription = this.$el.dataset.structuredDataPageDescription;
            if (this.$el.dataset.structuredDataPageUrl) this.dataStructuredDataPageUrl = this.$el.dataset.structuredDataPageUrl;

            if (structuredDataElement?.dataset.structuredDataScriptId) this.dataStructuredDataScriptId = structuredDataElement.dataset.structuredDataScriptId;
            if (structuredDataElement?.dataset.structuredDataPageName) this.dataStructuredDataPageName = structuredDataElement.dataset.structuredDataPageName;
            if (structuredDataElement?.dataset.structuredDataPageDescription) this.dataStructuredDataPageDescription = structuredDataElement.dataset.structuredDataPageDescription;
            if (structuredDataElement?.dataset.structuredDataPageUrl) this.dataStructuredDataPageUrl = structuredDataElement.dataset.structuredDataPageUrl;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            const pageValue: string = urlParams.get(this.constants.queryParams.page) ?? '';
            let pageParam = pageValue ? parseInt(pageValue) : 0;
            if (pageParam > this.pageLimit) pageParam = this.pageLimit;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            const textValue: string = urlParams.get(this.constants.queryParams.text) ?? '';
            this.searchText = textValue ?? '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            const filterParams = urlParams.toString() ? urlParams : null;
            this.filterParams = filterParams;

            // The code below is needed when facets have different operators.
            // Preset values for facets based on query params.
            //if (filterParams) {
            //    const facetsResponse = await this.fetchData();
            //    this.setFacets(facetsResponse);
            //    this.setFacetValuesFromUrl(filterParams);
            //}

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS.
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a: APIItem, b: APIItem) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            const tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData();
            const replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;
            this.filterParams = null;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        async fetchData(loading = true): Promise<AxiosResponse | null> {
            if (loading) this.debounceSetLoading(true);
            let response = null;
            try {
                response = await axios.post(this.dataApi, this.queryModel);
            } catch (error) {
                this.error = true;
                console.log(error);
            } finally {
                if (loading) this.debounceSetLoading(false);
            }
            return response;
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData(): Promise<void> {
            this.page = 1;
            const response = await this.fetchData();
            this.setData(response, true);
            this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore(): Promise<void> {
            this.loadingMore = true;
            this.page++;
            const response = await this.fetchData(false);
            this.setData(response);
            this.loadingMore = false;
            this.updateUrl();
        },
        // Method to set data items array or append items to it.
        setData(response: AxiosResponse | null, replace = false): void {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            }
            else {
                response.data.items.forEach((item: APIItem) => {
                    this.data.items.push(item);
                })
            }
            this.setFacets(response);
        },
        // The code below is needed when facets have different operators.
        // Method to set facet values based on URL search params.
        //setFacetValuesFromUrl(filterParams) {
        //    if (!this.facetsSet) return;
        //    this.data.facets.forEach(facet => {
        //        if (filterParams.has(facet.alias)) {
        //            let valueArray = filterParams.get(facet.alias).split(',');
        //            facet.values = valueArray
        //                .map(alias => facet.options.find(option => option.valueAlias === alias))
        //                .map(option => option.valueAlias);
        //        }
        //    });
        //},
        // Method to set facets array.
        setFacets(response: AxiosResponse | null): void {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a: Facet, b: Facet) => a.sortOrder - b.sortOrder)
                .map((facet: Facet) => ({
                    ...facet,
                    values: facet.options.filter((option: FacetOption) => option.selected).map((option: FacetOption) => option.valueAlias),
                }));
            this.data.queryCount = response.data.queryCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array: APIItem[]): APIItem[] {
            return array.reduce((accumulator: APIItem[], current: APIItem) => {
                if (!accumulator.find((item: APIItem) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        // Data alteration function for reloading data with set filters for checkboxes.
        toggleOption(facetAlias: string, optionAlias: string): void {
            const changedFacet: Facet | undefined = this.data.facets.find(facet => facet.alias === facetAlias);
            if (!changedFacet) return;
            if (changedFacet.values.includes(optionAlias)) {
                changedFacet.values = changedFacet.values.filter((value: string) => value !== optionAlias);
            } else {
                changedFacet.values.push(optionAlias);
            }
            this.fetchInitialData();
        },
        // Data alteration function for reloading data with set filters for dropdowns.
        selectOption(facetAlias: string, event: Event): void {
            const target = event.target as HTMLSelectElement;
            const optionAlias: string = target.value;
            const changedFacet: Facet | undefined = this.data.facets.find(facet => facet.alias === facetAlias);
            if (!changedFacet) return;
            if (optionAlias === '') {
                changedFacet.values = [];
            } else {
                changedFacet.values = [optionAlias];
            }
            this.fetchInitialData();
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset(): void {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.fetchInitialData();
            this.loading = false;
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl(): void {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, '', window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                this.queryModel.FilterItems.forEach((filterItem: any) => urlParams.append(filterItem.Key, filterItem.Values.join()))
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page.toString());
            history.pushState({}, '', window.location.pathname + '?' + urlParams.toString());
        },
        createDebouncedLoading() {
            return this.debounce((state: boolean) => {
                this.loading = state;
            }, 500);
        },
    },
});
